/**
 * Error translation helpers — turn raw network/Supabase failures into
 * VibecadeError instances with stable codes and safe messages.
 */
import { VibecadeError, VibecadeErrorCode } from '../types';

/**
 * Translate a failed fetch Response into a VibecadeError. Matches the
 * `{ error, message }` JSON shape Edge Functions return. Falls back to
 * NETWORK_ERROR with the HTTP status when the body can't be parsed.
 */
export async function errorFromResponse(res: Response): Promise<VibecadeError> {
  let code: string = VibecadeErrorCode.NetworkError;
  let message = `Request failed with status ${res.status}`;
  try {
    const json = (await res.json()) as Partial<{ error: string; message: string }>;
    if (typeof json.error === 'string' && json.error) code = json.error;
    if (typeof json.message === 'string' && json.message) message = json.message;
  } catch {
    // Body wasn't JSON or was empty — keep defaults.
  }
  return new VibecadeError(code, message);
}

/**
 * Wrap an arbitrary thrown value in a VibecadeError. If the value is
 * already a VibecadeError, pass it through unmodified. The original
 * value is attached to `cause` — it's never stringified into `message`
 * so internal details don't leak.
 */
export function wrapError(
  e: unknown,
  fallbackCode: string,
  fallbackMessage: string,
): VibecadeError {
  if (e instanceof VibecadeError) return e;
  return new VibecadeError(fallbackCode, fallbackMessage, e);
}
